import { useState } from 'react'
import { ApiError, withdrawPenCount, type PenCount } from '../api'
import { useT } from '../lang'

/* The farm's rings, on one day.
 *
 * WHY THIS TABLE IS THE PRODUCT AND THE SITE FIGURE IS NOT.
 *
 * Vedlegg 1 is written per merd — ten random fish from every ring, twenty in
 * the spring window, journalled per fish in three stage groups — and what goes
 * to Mattilsynet is the locality mean. The regulatory number is therefore a
 * lossy projection of what the farm is already obliged to hold, and a
 * spreadsheet stores the projection and throws the observation away.
 *
 * Everything worth having lives in what it discards, and this is where it
 * shows: which ring is dragging the average, whether that ring is really worse
 * or whether one fish had four lice on it, and what the farm's number would be
 * if only that ring were deloused. None of those questions has an answer at the
 * site level, and all three fall out of the same rows.
 *
 * **The site figure is fish-weighted, not the mean of merd means**, and the
 * footer says so when it matters. In the compliant case the two agree, because
 * Vedlegg 1 asks for the same number of fish from every ring. They diverge when
 * a ring was short-sampled — and then the fish-weighted mean is the one that
 * equals "lice found ÷ fish looked at", which is what the limit is expressed
 * against. Computed server-side in the write transaction; never here.
 */

/** Norwegian decimals, and a dash where there is no number.
 *
 *  A blank cell and a zero are different claims — "we did not count that
 *  stage" against "we counted and there were none" — so the empty one has to
 *  read as empty rather than as a number that happens to be small. */
function tall(v: number | null | undefined, d = 2): string {
  if (v === null || v === undefined) return '–'
  return v.toFixed(d).replace('.', ',')
}

/** Is this ring over the limit in force?
 *
 *  The limit is passed in per week rather than read once, because it is 0,50
 *  most of the year, 0,20 in weeks 16–21 and 0,10 there in a red production
 *  area. Reading it once and reusing it is invisible today and wrong in April.
 */
function over(v: number | null, grense: number | null): boolean {
  return v !== null && grense !== null && v >= grense
}

export function Merder({
  siteId, items, grense, canWrite, onItems, onChanged, onCount, selected,
  onSelect,
}: {
  siteId: string
  /* THE ROWS ARE HANDED IN, NOT FETCHED HERE, and that is the point of the
     change. The map above this table draws the same day from the same rows,
     and two components fetching the same URL is two components that disagree
     for a second after every write -- the ring goes green on the map while the
     row below it still reads the old number. One owner, one fetch, one truth. */
  items: PenCount[] | null
  /** The limit in force for the week this day falls in, or null if unknown. */
  grense: number | null
  canWrite: boolean
  /** A write happened here; the day's rows are now these. */
  onItems: (items: PenCount[]) => void
  onChanged: () => void
  /** Open the counting form at one ring. */
  onCount?: (penId: string) => void
  /** Kept in step with the map: clicking a ring highlights its row. */
  selected?: string | null
  onSelect?: (penId: string | null) => void
}) {
  const t = useT()
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  if (err) return <div className="notice bad">{err}</div>
  if (!items) return null
  // A farm whose merder nobody has entered yet counts at site level, exactly as
  // every farm did until now. Nothing to show, and nothing wrong.
  if (items.length === 0) return null

  const counted = items.filter((p) => p.adult_female !== null)
  const fish = counted.reduce((n, p) => n + (p.n_fish ?? 0), 0)
  // Fish-weighted, mirroring the server so the footer agrees with the row the
  // farm page shows. Recomputed rather than fetched because this component owns
  // one day and the roll-up on the write response only exists after a write.
  const snitt = fish > 0
    ? counted.reduce((s, p) => s + (p.adult_female ?? 0) * (p.n_fish ?? 0), 0) / fish
    : null
  const uneven = counted.length > 1
    && new Set(counted.map((p) => p.n_fish)).size > 1

  /* THE ONE RECOMMENDATION THIS PAGE MAKES, and the reason per-ring data is
     worth collecting at all.

     §9 of lakselusforskriften says a site over the limit is treated in ALL
     production units "med mindre det kan dokumenteres at behandling av alle
     produksjonsenhetene er åpenbart unødvendig". Documenting that is exactly
     what these rows are, so the arithmetic is put in front of somebody rather
     than left for them to do in their head.

     Computed here and NOT asked of the forecast model. The model's axis is
     locality-week — there is no ring in its panel — so a per-merd number
     dressed as a forecast would be inventing an authority it does not have.
     This is arithmetic over the farm's own counts and is labelled as ours. */
  const verste = counted.length > 1 && snitt !== null && over(snitt, grense)
    ? counted.reduce((a, b) =>
        (b.adult_female ?? 0) > (a.adult_female ?? 0) ? b : a)
    : null
  const utenVerste = verste
    ? (() => {
        const rest = counted.filter((p) => p.pen_id !== verste.pen_id)
        const n = rest.reduce((s, p) => s + (p.n_fish ?? 0), 0)
        return n > 0
          ? rest.reduce((s, p) => s + (p.adult_female ?? 0) * (p.n_fish ?? 0), 0) / n
          : null
      })()
    : null
  // Only worth saying if treating that one ring would actually bring the farm
  // under. "Delouse merd 3 and you go from 0,62 to 0,58" is not advice.
  const verdt = verste && utenVerste !== null && !over(utenVerste, grense)

  const withdraw = async (penCountId: string) => {
    setBusy(penCountId)
    try {
      const r = await withdrawPenCount(siteId, penCountId)
      // The rows, not r.site: the footer is recomputed from the rows so it is
      // right on first load too, before any write has happened. Two sources for
      // one number is how they come to disagree.
      onItems(r.items)
      onChanged()
    } catch (e) {
      setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="stack merder" style={{ gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 14 }}>{t('By pen')}</h3>

      <table className="tbl merdtabell">
        <thead>
          <tr>
            <th>{t('Pen')}</th>
            <th className="num">{t('Adult female')}</th>
            <th className="num">{t('Mobile')}</th>
            <th className="num">{t('Sessile')}</th>
            <th className="num">{t('Fish')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const hot = over(p.adult_female, grense)
            const cls = [hot ? 'over' : '', selected === p.pen_id ? 'valgt' : '']
              .filter(Boolean).join(' ')
            return (
              <tr key={p.pen_id} className={cls || undefined}
                onClick={onSelect
                  ? () => onSelect(selected === p.pen_id ? null : p.pen_id)
                  : undefined}>
                <td data-l={t('Pen')}>
                  <strong>{p.pen_no}</strong>
                  {p.name && <span className="faint"> {p.name}</span>}
                  {/* A ring that has since left the array. Said quietly and
                      on the row, because the fish were real and the site
                      figure still counts them -- but nobody should look for
                      this merd on the map or on next week's sheet. */}
                  {p.retired && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      {' · '}{t('retired')}
                    </span>
                  )}
                  {/* Said on the row it belongs to. A skipped ring is a
                      reported fact, and the reason is what Mattilsynet asks
                      for. */}
                  {p.not_counted_reason && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      {' · '}{t(`skipped:${p.not_counted_reason}`)}
                    </span>
                  )}
                </td>
                <td className="num" data-l={t('Adult female')}>
                  {tall(p.adult_female)}
                  {/* The spread, where the fish were written down. This is the
                      whole prize of counting per fish: it separates "merd 3 is
                      over" from "merd 3 had one fish with four lice on it". */}
                  {p.adult_female_var !== null && p.n_fish ? (
                    <span className="faint" style={{ fontSize: 11 }}>
                      {' ± '}
                      {tall(Math.sqrt(p.adult_female_var / p.n_fish))}
                    </span>
                  ) : null}
                </td>
                <td className="num" data-l={t('Mobile')}>{tall(p.mobile)}</td>
                <td className="num" data-l={t('Sessile')}>{tall(p.sessile)}</td>
                <td className="num" data-l={t('Fish')}>
                  {p.n_fish ?? '–'}
                  {/* Which layer this row came from, said quietly. A ring whose
                      fish were listed is a stronger record than one whose mean
                      was typed, and flattening the two would hide it. */}
                  {p.basis === 'derived' && (
                    <span className="faint" title={t('Counted fish by fish')}>
                      {' '}·
                    </span>
                  )}
                </td>
                <td className="num">
                  {p.pen_count_id && canWrite && (
                    <button className="btn ghost sm" disabled={!!busy}
                      onClick={() => void withdraw(p.pen_count_id!)}>
                      {t('Take it back')}
                    </button>
                  )}
                  {!p.pen_count_id && canWrite && onCount && (
                    <button className="btn sm"
                      onClick={() => onCount(p.pen_id)}>
                      {t('Count')}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td><strong>{t('The site')}</strong></td>
            <td className="num">
              <strong className={over(snitt, grense) ? 'over' : undefined}>
                {tall(snitt)}
              </strong>
            </td>
            <td className="num" colSpan={2}>
              <span className="faint">
                {counted.length}/{items.length} {t('pens')}
              </span>
            </td>
            <td className="num">{fish || '–'}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>

      {/* Only said when the two arithmetics would actually disagree. Saying it
          every week would train people to stop reading it. */}
      {uneven && (
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('The pens gave different numbers of fish, so the site figure is weighted by fish counted — not the average of the pen averages.')}
        </p>
      )}

      {verdt && verste && (
        <div className="notice">
          <strong>{t('Pen')} {verste.pen_no}</strong>{' '}
          {t('is what puts the site over.')}{' '}
          {t('Treating only that one brings the site to')}{' '}
          <strong>{tall(utenVerste)}</strong>.{' '}
          <span className="muted">
            {t('Our arithmetic on your own counts, not a forecast — and §9 wants it documented before you treat one pen instead of all of them.')}
          </span>
        </div>
      )}
    </div>
  )
}
